import 'rsuite/dist/rsuite.min.css';
import {CustomProvider} from "rsuite";
import "./tree.css"
import { Navigate, Outlet } from 'react-router-dom';
import Sidebar from '../components/common/sidebar/sidebar';
import Header from '../components/common/header/header';
import { appStore, stopViewingAs, handleImpersonationExpired } from '@store/appStore';
import { useSnapshot } from 'valtio';
import { useEffect, useState } from 'react';
import ActiveJobsToast from '@components/ActiveJobsToast';
import ImpersonationPermissionsModal from './ImpersonationPermissionsModal';
import motimaticLogo from "../assets/images/brand-logos/logoMotimatic.svg";
import { bannerPreviewStore } from "@/modules/dashboard/banner/bannerPreviewStore";
import BannerPreviewShellBar from "@/modules/dashboard/banner/BannerPreviewShellBar";

function formatRemaining(expiresAt: string): string {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return "0:00";
    const totalSeconds = Math.floor(ms / 1000);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}

function ImpersonationBanner() {
    const { viewingAsUser, impersonationExpiresAt } = useSnapshot(appStore);
    const [permModalOpen, setPermModalOpen] = useState(false);
    const [, setTick] = useState(0);

    // Tick every second to refresh the countdown; auto-stop when it hits 0.
    useEffect(() => {
        if (!viewingAsUser || !impersonationExpiresAt) return;
        const interval = setInterval(() => {
            const remaining = new Date(impersonationExpiresAt).getTime() - Date.now();
            if (remaining <= 0) {
                handleImpersonationExpired();
                return;
            }
            setTick((t) => t + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [viewingAsUser, impersonationExpiresAt]);

    if (!viewingAsUser) return null;

    return (
        <>
            <div
                style={{
                    position: "fixed",
                    bottom: 16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 9999,
                    background: "#191c40",
                    color: "#fff",
                    padding: "8px 20px",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                    fontSize: "0.875rem",
                }}
            >
                <i className="bi bi-eye" style={{ fontSize: "1rem" }}></i>
                <span
                    onClick={() => setPermModalOpen(true)}
                    style={{ cursor: "pointer" }}
                >
                    Viewing as <strong>{viewingAsUser}</strong>
                </span>
                {impersonationExpiresAt && (
                    <span style={{ opacity: 0.7, fontVariantNumeric: "tabular-nums" }}>
                        {formatRemaining(impersonationExpiresAt)}
                    </span>
                )}
                <button
                    onClick={() => stopViewingAs()}
                    style={{
                        background: "rgba(255,255,255,0.15)",
                        border: "none",
                        color: "#fff",
                        padding: "4px 12px",
                        borderRadius: 4,
                        cursor: "pointer",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                    }}
                >
                    Stop
                </button>
            </div>
            <ImpersonationPermissionsModal
                open={permModalOpen}
                onClose={() => setPermModalOpen(false)}
            />
        </>
    );
}

interface AppProps {
    hasSidebar?: boolean
    noChrome?: boolean
}

export default function App({ hasSidebar = false, noChrome = false }: AppProps) {
    // A banner preview (MPN-2266) puts a fixed bar above the shell; null leaves the shell as it is.
    const previewing = useSnapshot(bannerPreviewStore).preview !== null;

    if (appStore.authToken === "") {
        return <Navigate to="/" replace />;
    }

    if (noChrome) {
        return (
            <CustomProvider theme="dark">
                <div className="d-flex flex-column vh-100 overflow-hidden" style={{ background: "#f9f9f9" }}>
                    <ActiveJobsToast />
                    <Outlet />
                </div>
            </CustomProvider>
        );
    }

    return (
        <CustomProvider theme="dark">
            {previewing && <BannerPreviewShellBar />}
            <div
                className="d-flex vh-100 overflow-hidden"
                style={previewing ? { paddingTop: "var(--banner-preview-bar-h)" } : undefined}
            >
                <ActiveJobsToast />
                <ImpersonationBanner />

                {/* Sidebar column */}
                {hasSidebar && (
                    <div
                        className="d-flex flex-column"
                        style={{
                            width: 240,
                            backgroundColor: "#191c40",
                            flexShrink: 0,
                        }}
                    >
                        {/* Logo */}
                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{ height: "60px", flexShrink: 0 }}
                        >
                            <img src={motimaticLogo} alt="logo" width={150} />
                        </div>

                        {/* Nav items */}
                        <div style={{ overflowY: "auto", overflowX: "hidden", flex: 1 }}>
                            <Sidebar />
                        </div>
                    </div>
                )}

                {/* Right column: header + content */}
                <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                    <div style={{ height: "60px", flexShrink: 0 }}>
                        <Header />
                    </div>
                    <main
                        className="flex-grow-1"
                        style={{
                            background: "#f9f9f9",
                            overflowY: "auto",
                            minHeight: 0,
                        }}
                    >
                        <Outlet />
                    </main>
                </div>
            </div>
        </CustomProvider>
    );
}
